/**
 * Cloudflare Turnstile (bot check on public, unauthenticated forms).
 *
 * The API owns the configuration (`VELORA_TURNSTILE_*`, see
 * docs/security/turnstile.md) and serves the public half of it at runtime,
 * so a web build never bakes in a site key:
 *
 *   GET /v1/system/public-config          staff web app (sign-in, forms links)
 *   GET /v1/booking/public/sites/:slug    `turnstile` on the booking page's site
 *
 * A protected request carries the widget token as `turnstileToken` in its
 * JSON body (or the `x-turnstile-token` header). Each token works once.
 */
import { z } from 'zod';

export const TURNSTILE_MODES = ['off', 'monitor', 'enforce'] as const;
export const TurnstileModeSchema = z.enum(TURNSTILE_MODES);
export type TurnstileMode = z.infer<typeof TurnstileModeSchema>;

/**
 * The `action` each protected form renders its widget with; the API checks
 * siteverify returns the same one, so a token solved on one form cannot be
 * spent on another. Cloudflare allows `[A-Za-z0-9_-]{1,32}`.
 */
export const TURNSTILE_ACTIONS = {
  login: 'login',
  formsVerify: 'forms_verify',
  formsWebStart: 'forms_web_start',
  bookingRequest: 'booking_request',
  bookingCallback: 'booking_callback',
  bookingIdentify: 'booking_identify',
  chatStart: 'chat_start',
  portalSignIn: 'portal_sign_in',
  portalContact: 'portal_contact',
} as const;
export type TurnstileAction = (typeof TURNSTILE_ACTIONS)[keyof typeof TURNSTILE_ACTIONS];

/** What the browser needs: no site key means render nothing. */
export const TurnstilePublicSchema = z.object({
  siteKey: z.string().nullable(),
  /** `enforce`: a request without a valid token is refused. `monitor`: logged, allowed. */
  mode: TurnstileModeSchema,
});
export type TurnstilePublic = z.infer<typeof TurnstilePublicSchema>;

export const TURNSTILE_OFF: TurnstilePublic = { siteKey: null, mode: 'off' };

/** Optional token field on protected request bodies. */
export const TurnstileTokenSchema = z.string().max(4096).optional();

export const TURNSTILE_TOKEN_HEADER = 'x-turnstile-token';

export const PublicConfigSchema = z.object({ turnstile: TurnstilePublicSchema });
export type PublicConfig = z.infer<typeof PublicConfigSchema>;
